/**
 * PostEventReflectionModal.jsx
 * 
 * Private, optional reflection modal shown at end of:
 * - Text Speed Dating sessions
 * - Lifestyle Lounge (on leave)
 * - Video Speed Dating (ended event)
 * 
 * NEVER blocks exit. Failures are silent.
 */

import React, { useState, useEffect, useRef, useContext } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { AuthContext } from "../../App";
import { track } from "../../utils/analytics";

const API = window.location.origin + "/api";

// Rating options (1-5 stars)
const RATINGS = [1, 2, 3, 4, 5];

// Feelings chips (max 2)
const FEELINGS_OPTIONS = [
  { value: "seen", label: "Seen", emoji: "👀" },
  { value: "curious", label: "Curious", emoji: "🤔" },
  { value: "excited", label: "Excited", emoji: "✨" },
  { value: "unsure", label: "Unsure", emoji: "😕" },
  { value: "overwhelmed", label: "Overwhelmed", emoji: "😮‍💨" },
  { value: "not_my_vibe", label: "Not my vibe", emoji: "🙅" },
];

// Next move options
const NEXT_MOVE_OPTIONS = [
  { value: "message_someone", label: "Message someone", emoji: "💬" },
  { value: "save_for_later", label: "Save for later", emoji: "📌" },
  { value: "take_break", label: "Take a break", emoji: "☕" },
  { value: "jump_back_in", label: "Jump back in", emoji: "🔄" },
];

/**
 * PostEventReflectionModal
 * 
 * @param {boolean} open - Whether modal is open
 * @param {function} onClose - Called when modal closes (Save or Skip)
 * @param {string} eventType - "text_speed_dating" | "lounge" | "video_speed_dating"
 * @param {string} eventId - Optional event/room ID
 */
export function PostEventReflectionModal({ open, onClose, eventType, eventId }) {
  const { token } = useContext(AuthContext);
  
  // Form state
  const [rating, setRating] = useState(0);
  const [feelings, setFeelings] = useState([]);
  const [nextMove, setNextMove] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  
  // Track reflection_shown only once per open
  const hasTrackedShownRef = useRef(false);
  
  useEffect(() => {
    if (open && !hasTrackedShownRef.current) {
      track("reflection_shown", { event_type: eventType });
      hasTrackedShownRef.current = true;
    }
    
    // Reset tracking when modal closes
    if (!open) {
      hasTrackedShownRef.current = false;
    }
  }, [open, eventType]);
  
  // Toggle feeling (max 2)
  const toggleFeeling = (value) => {
    setFeelings((prev) => {
      if (prev.includes(value)) {
        return prev.filter((f) => f !== value);
      }
      if (prev.length >= 2) {
        // Replace oldest with new
        return [prev[1], value];
      }
      return [...prev, value];
    });
  };
  
  // Save reflection
  const handleSave = async () => {
    if (!rating || !nextMove) {
      // Silently skip if incomplete - don't block
      handleSkip();
      return;
    }
    
    setSaving(true);
    
    try {
      await fetch(`${API}/reflections`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          event_type: eventType,
          event_id: eventId || null,
          rating,
          feelings,
          next_move: nextMove,
          note: note.trim() || null,
        }),
      });
      
      // Analytics tracked by backend on success
    } catch (err) {
      // Silent failure - do NOT block exit
      console.warn("[Reflection] Save failed (non-blocking):", err);
    } finally {
      setSaving(false);
      onClose?.();
    }
  };
  
  // Skip reflection
  const handleSkip = () => {
    track("reflection_skipped", { event_type: eventType });
    onClose?.();
  };
  
  // Get event type label for display
  const getEventLabel = () => {
    switch (eventType) {
      case "text_speed_dating":
        return "text date";
      case "lounge":
        return "lounge session";
      case "video_speed_dating":
        return "video event";
      default:
        return "session";
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleSkip()}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl text-white">
            How was your {getEventLabel()}?
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Quick reflection (private, optional)
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Rating */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Overall experience
            </label>
            <div className="flex gap-2">
              {RATINGS.map((r) => (
                <button
                  key={r}
                  onClick={() => setRating(r)}
                  className={`w-10 h-10 rounded-full text-lg transition-all ${
                    rating >= r
                      ? "bg-amber-500 text-white"
                      : "bg-slate-800 text-slate-500 hover:bg-slate-700"
                  }`}
                  data-testid={`rating-star-${r}`}
                >
                  ★
                </button>
              ))}
            </div>
          </div>

          {/* Feelings chips */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              How did you feel? <span className="text-slate-500">(pick up to 2)</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {FEELINGS_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => toggleFeeling(opt.value)}
                  className={`px-3 py-1.5 rounded-full text-sm transition-all flex items-center gap-1.5 ${
                    feelings.includes(opt.value)
                      ? "bg-purple-500 text-white"
                      : "bg-slate-800 text-slate-300 hover:bg-slate-700"
                  }`}
                  data-testid={`feeling-${opt.value}`}
                >
                  <span>{opt.emoji}</span>
                  <span>{opt.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Next move */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              What&apos;s next for you?
            </label>
            <div className="grid grid-cols-2 gap-2">
              {NEXT_MOVE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setNextMove(opt.value)}
                  className={`px-3 py-2 rounded-lg text-sm transition-all flex items-center gap-2 ${
                    nextMove === opt.value
                      ? "bg-rose-500 text-white"
                      : "bg-slate-800 text-slate-300 hover:bg-slate-700"
                  }`}
                  data-testid={`next-move-${opt.value}`}
                >
                  <span>{opt.emoji}</span>
                  <span>{opt.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Optional note */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Anything else? <span className="text-slate-500">(optional)</span>
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 240))}
              placeholder="A thought, a name you want to remember..."
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white placeholder:text-slate-500 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-purple-500/50"
              rows={2}
              maxLength={240}
              data-testid="reflection-note"
            />
            <p className="text-xs text-slate-500 mt-1 text-right">
              {note.length}/240
            </p>
          </div>
        </div>

        <DialogFooter className="flex gap-3 sm:gap-3">
          <button
            onClick={handleSkip}
            disabled={saving}
            className="flex-1 py-2.5 rounded-lg bg-slate-800 text-slate-300 hover:bg-slate-700 transition-colors disabled:opacity-50"
            data-testid="reflection-skip-btn"
          >
            Skip
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !rating || !nextMove}
            className="flex-1 py-2.5 rounded-lg bg-gradient-to-r from-purple-500 to-rose-500 text-white font-medium hover:from-purple-600 hover:to-rose-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="reflection-save-btn"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default PostEventReflectionModal;
